/**
 * Play a notification sound to alert the user
 * Uses Web Audio API to generate a pleasant pop/beep sound
 * Plays even when tab is not in focus to help keep conversations flowing
 */
export const playNotificationSound = (): void => {
  // Play sound regardless of tab focus - helps users know about messages even when on other tabs

  try {
    // Check if AudioContext is available
    const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContext) {
      // Fallback: try using HTML5 Audio with data URI
      playFallbackSound();
      return;
    }

    const audioContext = new AudioContext();

    // Create a pleasant "pop" sound using oscillators
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Configure the sound - a pleasant pop/ding sound
    // Start at a higher frequency and quickly drop for a "pop" effect
    oscillator.frequency.setValueAtTime(1000, audioContext.currentTime); // Start at 1000Hz
    oscillator.frequency.exponentialRampToValueAtTime(600, audioContext.currentTime + 0.15); // Drop to 600Hz

    // Envelope for a quick, pleasant pop sound
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.4, audioContext.currentTime + 0.01); // Quick attack
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15); // Quick decay

    oscillator.type = "sine"; // Use sine wave for a smoother sound

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.15);

    // Clean up after sound finishes
    oscillator.onended = () => {
      audioContext.close();
    };
  } catch (error) {
    console.warn("Failed to play notification sound:", error);
    // Fallback to simple beep
    playFallbackSound();
  }
};

/**
 * Fallback sound using HTML5 Audio with data URI
 * Creates a simple beep sound
 * Plays even when tab is not in focus
 */
const playFallbackSound = (): void => {
  // Play sound regardless of tab focus

  try {
    // Create a simple beep using a data URI (base64 encoded WAV)
    // This is a very short beep sound
    const audio = new Audio();

    // Use a data URI for a simple beep (1000Hz sine wave, 0.15 seconds)
    // This is a minimal WAV file encoded as base64
    const beepDataUri = "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIGWi77+efTRAMUKfj8LZjHAY4kdfyzHksBSR3x/DdkEAKFF606euoVRQKRp/g8r5sIQUrgc7y2Yk2CBlou+/nn00QDFCn4/C2YxwGOJHX8sx5LAUkd8fw3ZBACg==";

    audio.src = beepDataUri;
    audio.volume = 0.4; // Set volume to 40%
    audio.play().catch((err) => {
      console.warn("Failed to play fallback sound:", err);
    });
  } catch (error) {
    console.warn("Fallback sound failed:", error);
  }
};

